import { type FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { api } from '../lib/api-client';

/**
 * «Esqueci minha senha» (Item 477) — pública. A resposta do servidor é a mesma existindo ou não a
 * conta; a tela também. Só o que muda é o aviso quando o ambiente não envia e-mail: aí o caminho é
 * pedir o link a quem gerencia os usuários.
 */
export function EsqueciSenhaPage(): JSX.Element {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState<{ emailConfigured: boolean } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.title = 'Lume — Esqueci minha senha';
  }, []);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const r = await api.forgotPassword(email.trim());
      setDone({ emailConfigured: r.emailConfigured });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro inesperado.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-app-bg px-4">
      <div className="w-full max-w-sm space-y-6 rounded-lg bg-surface p-8 shadow-md border border-border-subtle">
        <div className="space-y-1 text-center">
          <h1 className="text-xl font-semibold tracking-tight text-text-primary">
            Esqueci minha senha
          </h1>
          <p className="text-sm text-text-secondary">
            Diga seu e-mail e a gente manda um link para criar uma senha nova.
          </p>
        </div>

        {done === null ? (
          <form className="space-y-4" onSubmit={(e) => void handleSubmit(e)} noValidate>
            <label className="block space-y-1">
              <span className="text-sm font-medium text-text-primary">E-mail</span>
              <input
                type="email"
                required
                autoComplete="username"
                className="input w-full"
                aria-label="E-mail"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={submitting}
              />
            </label>
            {error !== null && (
              <p role="alert" className="text-sm text-red-600">
                {error}
              </p>
            )}
            <button
              type="submit"
              className="btn btn--primary w-full"
              disabled={submitting || !email.includes('@')}
            >
              {submitting ? 'Enviando…' : 'Enviar link'}
            </button>
          </form>
        ) : (
          <div className="space-y-3 text-center" role="status">
            <p className="text-sm text-text-primary">
              Se existir uma conta com <strong>{email.trim()}</strong>, o link para criar uma senha
              nova vale por 1 hora.
            </p>
            {done.emailConfigured ? (
              <p className="text-xs text-text-secondary">Confira sua caixa de entrada e o spam.</p>
            ) : (
              <p className="text-xs text-text-secondary" data-testid="email-off-hint">
                Neste ambiente o e-mail automático está desligado. Peça o link a quem gerencia os
                usuários da sua empresa — ele consegue gerar um na sua ficha.
              </p>
            )}
          </div>
        )}

        <p className="text-center text-sm">
          <Link to="/login" className="text-text-secondary underline underline-offset-2">
            Voltar para o login
          </Link>
        </p>
      </div>
    </div>
  );
}
